import * as React from "react"
import { X, Check, Search } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/utils/utils"

export interface Option {
  value: string
  label: string
}

interface MultiSelectProps {
  options: Option[]
  selected: string[]
  onChange: (values: string[]) => void
  className?: string
  placeholder?: string
}

export function MultiSelect({
  options = [],
  selected = [],
  onChange,
  className,
  placeholder = "Select options...",
  ...props
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [searchValue, setSearchValue] = React.useState("")
  const containerRef = React.useRef<HTMLDivElement>(null)

  const filteredOptions = React.useMemo(() => {
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchValue.toLowerCase())
    )
  }, [options, searchValue])

  const handleUnselect = (value: string) => {
    onChange(selected.filter((v) => v !== value))
  }

  const handleSelect = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value))
    } else {
      onChange([...selected, value])
    }
  }

  // Ferme le menu si on clique en dehors
  React.useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
        setSearchValue("")
      }
    }

    document.addEventListener("mousedown", handleOutsideClick)
    return () => document.removeEventListener("mousedown", handleOutsideClick)
  }, [])

  const displayText = React.useMemo(() => {
    if (selected.length === 0) return placeholder
    if (selected.length === 1) {
      const option = options.find(opt => opt.value === selected[0])
      return option?.label || selected[0]
    }
    return `${selected.length} sélectionné${selected.length > 1 ? 's' : ''}`
  }, [selected, options, placeholder])

  return (
    <div ref={containerRef} className={cn("relative w-full", className)} {...props}>
      {/* Trigger Button */}
      <button
        type="button"
        className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        onClick={() => setOpen(!open)}
      >
        <span className="truncate text-left">{displayText}</span>
        <svg
          className={cn("h-4 w-4 opacity-50 transition-transform", open && "rotate-180")}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6,9 12,15 18,9" />
        </svg>
      </button>

      {/* Selected items as badges (if any selected and dropdown closed) */}
      {selected.length > 0 && !open && (
        <div className="flex flex-wrap gap-1 mt-1">
          {selected.slice(0, 3).map((value) => {
            const option = options.find((opt) => opt.value === value)
            return (
              <Badge key={value} variant="secondary" className="text-xs">
                {option?.label ?? value}
                <button
                  className="ml-1 hover:bg-secondary-foreground/20 rounded-full"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleUnselect(value)
                  }}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )
          })}
          {selected.length > 3 && (
            <Badge variant="secondary" className="text-xs">
              +{selected.length - 3} plus
            </Badge>
          )}
        </div>
      )}

      {/* Dropdown Content */}
      {open && (
        <div className="absolute z-50 mt-1 bg-popover border rounded-md shadow-lg dark:glow-border min-w-[300px] w-full max-w-[500px]">
          {/* Search Bar */}
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                className="w-full pl-9 pr-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Rechercher des domaines..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                autoFocus
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-[250px] overflow-auto">
            {filteredOptions.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                {searchValue ? "Aucun domaine trouvé" : "Aucune option disponible"}
              </div>
            ) : (
              <div className="p-2">
                {filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className={cn(
                      "flex items-center px-3 py-2 cursor-pointer hover:bg-accent rounded-sm transition-colors",
                      selected.includes(option.value) && "bg-accent"
                    )}
                    onClick={() => handleSelect(option.value)}
                  >
                    <div className={cn(
                      "flex items-center justify-center w-4 h-4 mr-3 border rounded-sm flex-shrink-0",
                      selected.includes(option.value) 
                        ? "bg-primary border-primary text-primary-foreground" 
                        : "border-muted-foreground/30"
                    )}>
                      {selected.includes(option.value) && (
                        <Check className="w-3 h-3" />
                      )}
                    </div>
                    <span className="text-sm flex-1 text-left">{option.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer with selection count */}
          {selected.length > 0 && (
            <div className="p-3 border-t bg-muted/50">
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>{selected.length} élément{selected.length > 1 ? 's' : ''} sélectionné{selected.length > 1 ? 's' : ''}</span>
                <button
                  className="text-primary hover:text-primary/80 font-medium"
                  onClick={() => onChange([])}
                >
                  Tout désélectionner
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export { MultiSelect as MultiSelectTrigger, MultiSelect as MultiSelectContent, MultiSelect as MultiSelectItem, MultiSelect as MultiSelectValue }
